import { formatPairingCode } from '@forgesworn/joystick-protocol';
/**
 * Tank Royale renderer.
 *
 * Single 2D canvas. Letterbox-scale the ARENA_W x ARENA_H world into
 * whatever the window is so the arena always fits regardless of TV
 * aspect ratio. Overlay HUD elements (pairing tiles, scoreboard,
 * winner banner) on top in screen space.
 */

import {
  ARENA_W, ARENA_H, BULLET_RADIUS, COUNTDOWN_S, MAX_PLAYERS, PLAYER_COLOURS,
  HUMAN_MAX_HP, SHIELD_COOLDOWN_S, SHIELD_DURATION_S, TANK_MAX_HP, TANK_RADIUS,
  WALLS, humanCount, type Bullet, type Tank, type World,
} from './game.js';

interface Geom { scale: number; offsetX: number; offsetY: number }

export interface CheerBurst {
  slot: number;
  /** performance.now() millisecond timestamp after which the burst expires. */
  until: number;
  colour: string;
}

export interface RenderFrame {
  world: World;
  pairingUrl: string;
  qrDataUrl: string | null;
  /** The pairing code (the session id). Printed under the QR as the
   *  type-it-in fallback for players who cannot scan. */
  sessionId: string;
  /** Slot → join state. 'open' means the QR is live and that slot is
   *  still recruitable; 'taken' means a phone is on this slot. */
  joinState: Map<number, 'open' | 'taken'>;
  /** True when audio is muted — drives the corner mute hint. */
  muted: boolean;
  /** Slot → running cross-game tournament total announced by the phone. */
  tournamentTotals: Map<number, number>;
  /** Slot → optional display name announced by the phone. */
  playerNames: Map<number, string>;
  /** Active cheer bursts from spectator phones. */
  cheers: CheerBurst[];
}

export function fitCanvas(canvas: HTMLCanvasElement): { scale: number; offsetX: number; offsetY: number } {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const ww = window.innerWidth;
  const wh = window.innerHeight;
  canvas.width = Math.floor(ww * dpr);
  canvas.height = Math.floor(wh * dpr);
  canvas.style.width = `${ww}px`;
  canvas.style.height = `${wh}px`;
  const scaleX = (ww / ARENA_W);
  const scaleY = (wh / ARENA_H);
  const scale = Math.min(scaleX, scaleY);
  const offsetX = (ww - ARENA_W * scale) / 2;
  const offsetY = (wh - ARENA_H * scale) / 2;
  return { scale: scale * dpr, offsetX: offsetX * dpr, offsetY: offsetY * dpr };
}

export function draw(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  const geom = fitCanvas(ctx.canvas);
  const canvas = ctx.canvas;
  ctx.fillStyle = '#06060c';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Screen-shake offset (world-space px). Translate the world-space
  // transform but leave the screen-space overlays steady.
  let shakeX = 0, shakeY = 0;
  if (frame.world.shake) {
    const elapsed = frame.world.time - frame.world.shake.startedAt;
    const t = Math.max(0, 1 - elapsed / frame.world.shake.duration);
    const amp = frame.world.shake.intensity * t * t;
    shakeX = (Math.random() * 2 - 1) * amp;
    shakeY = (Math.random() * 2 - 1) * amp;
  }

  ctx.save();
  ctx.translate(geom.offsetX + shakeX * geom.scale, geom.offsetY + shakeY * geom.scale);
  ctx.scale(geom.scale, geom.scale);

  drawArenaBackground(ctx);
  drawAmbientDrift(ctx, frame.world.time);
  drawTreads(ctx, frame);
  drawWalls(ctx);
  for (const bullet of frame.world.bullets) drawBullet(ctx, bullet);
  for (const tank of frame.world.tanks.values()) drawTank(ctx, tank, frame.world.time);
  drawParticles(ctx, frame);
  drawFlashes(ctx, frame);

  ctx.restore();

  // Screen-space overlays: a per-corner status pass (open badge or
  // score badge) plus one shared hero QR, the single scan target for
  // the whole table.
  drawCorners(ctx, frame, geom);
  drawHeroQr(ctx, frame, geom);
  drawLobbyOverlay(ctx, frame);
  drawAttractOverlay(ctx, frame);
  drawCountdownOverlay(ctx, frame);
  drawWinnerOverlay(ctx, frame);
  drawCheers(ctx, frame);
  drawMuteHint(ctx, frame);
}

function drawAttractOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (!frame.world.attractMode) return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const time = frame.world.time;
  const breath = 0.7 + 0.3 * Math.abs(Math.sin(time * 1.6));
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Banner across the very top of the screen — out of the way of the
  // bot action, but visible enough that the demo nature is clear.
  ctx.font = `bold ${Math.floor(18 * dpr)}px ui-monospace, monospace`;
  ctx.fillStyle = `rgba(255, 216, 74, ${0.5 + 0.5 * breath})`;
  ctx.shadowColor = '#ffd84a';
  ctx.shadowBlur = 10 * dpr * breath;
  ctx.fillText('ATTRACT MODE · SCAN ANY QR TO TAKE OVER', ctx.canvas.width / 2, 32 * dpr);
  ctx.shadowBlur = 0;
  ctx.restore();
}

function drawCheers(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.cheers.length === 0) return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const now = performance.now();
  const w = ctx.canvas.width;
  // Each cheer drives a brief glowing chevron at the bottom of the
  // screen plus a fading banner that names the spectator.
  ctx.save();
  for (const cheer of frame.cheers) {
    const remainingMs = Math.max(0, cheer.until - now);
    const t = remainingMs / 1200;       // 1 at start, 0 at expiry
    const alpha = Math.min(1, t * 1.4);
    const baseY = ctx.canvas.height - 70 * dpr;
    ctx.globalAlpha = alpha;
    // Coloured horizontal pulse at the bottom edge.
    const grad = ctx.createLinearGradient(0, baseY, w, baseY);
    grad.addColorStop(0, 'transparent');
    grad.addColorStop(0.5, cheer.colour);
    grad.addColorStop(1, 'transparent');
    ctx.fillStyle = grad;
    ctx.fillRect(0, baseY, w, 4 * dpr);
    // Banner text.
    ctx.fillStyle = cheer.colour;
    ctx.font = `bold ${Math.floor(24 * dpr)}px ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = cheer.colour;
    ctx.shadowBlur = 18 * dpr;
    ctx.fillText(`P${cheer.slot + 1} CHEERS`, w / 2, baseY - 28 * dpr);
    ctx.shadowBlur = 0;
  }
  ctx.restore();
}

function drawCountdownOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.round.kind !== 'countdown') return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const elapsed = frame.world.time - frame.world.round.startedAt;
  const remaining = COUNTDOWN_S - elapsed;
  // Map remaining time to a number 3 / 2 / 1 / GO.
  let label: string;
  let beatT: number;
  if (remaining > 2.2) { label = '3'; beatT = (COUNTDOWN_S - remaining); }
  else if (remaining > 1.2) { label = '2'; beatT = 1.0 - (remaining - 1.2); }
  else if (remaining > 0.2) { label = '1'; beatT = 1.0 - (remaining - 0.2); }
  else { label = 'GO!'; beatT = 1.0 - (remaining / 0.2); }
  // Pulse the size + glow with each beat. Cube the easing so the
  // number snaps in fast then settles.
  const pulse = 1 - Math.pow(Math.max(0, beatT - 0.05) / 0.95, 2);
  const size = Math.floor((90 + 30 * pulse) * dpr);
  const cx = ctx.canvas.width / 2;
  const cy = ctx.canvas.height / 2;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${size}px ui-monospace, monospace`;
  const colour = label === 'GO!' ? '#58ff58' : '#ffd84a';
  ctx.shadowColor = colour;
  ctx.shadowBlur = 32 * dpr;
  ctx.fillStyle = colour;
  ctx.fillText(label, cx, cy);
  ctx.restore();
}

function drawMuteHint(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  const dpr = ctx.canvas.width / window.innerWidth;
  ctx.save();
  ctx.font = `${Math.floor(11 * dpr)}px ui-monospace, monospace`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  const x = ctx.canvas.width - 16 * dpr;
  const y = 16 * dpr;
  ctx.fillStyle = frame.muted ? '#ffd84a' : 'rgba(207,216,255,0.55)';
  ctx.fillText(frame.muted ? 'MUTED · M' : 'AUDIO · M', x, y);
  ctx.restore();
}

function drawLobbyOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.round.kind !== 'lobby') return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const humans = humanCount(frame.world);
  const time = frame.world.time;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = ctx.canvas.width / 2;
  const cy = ctx.canvas.height / 2 - 20 * dpr;
  if (humans === 0) {
    // The hero QR owns the centre of the screen; the headline above it
    // is the game title, breathing 0.96 to 1.04 over 2.4s for life.
    const breath = 1 + Math.sin(time * 2.6) * 0.04;
    ctx.save();
    ctx.translate(cx, ctx.canvas.height * 0.15);
    ctx.scale(breath, breath);
    ctx.font = `bold ${Math.floor(46 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#cfd8ff';
    ctx.shadowColor = '#58ff58';
    ctx.shadowBlur = (18 + 8 * Math.sin(time * 3)) * dpr;
    ctx.fillText('TANK ROYALE', 0, 0);
    ctx.shadowBlur = 0;
    ctx.restore();
  } else {
    ctx.font = `bold ${Math.floor(56 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#58ff58';
    ctx.shadowColor = '#58ff58';
    ctx.shadowBlur = 22 * dpr;
    ctx.fillText('PRESS ANY BUTTON TO START', cx, cy);
    ctx.shadowBlur = 0;
    ctx.font = `${Math.floor(22 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#8a92b8';
    ctx.fillText(`${humans} ready · bots will fill the rest`, cx, cy + 56 * dpr);
  }
  ctx.restore();
}

function drawArenaBackground(ctx: CanvasRenderingContext2D): void {
  // Radial floor gradient — slight vignette so the centre feels lit
  // and the corners recede.
  const grad = ctx.createRadialGradient(ARENA_W / 2, ARENA_H / 2, 50, ARENA_W / 2, ARENA_H / 2, ARENA_W * 0.7);
  grad.addColorStop(0, '#13132a');
  grad.addColorStop(0.7, '#0c0c1a');
  grad.addColorStop(1, '#06060e');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, ARENA_W, ARENA_H);
  // Subtle grid.
  ctx.strokeStyle = 'rgba(120, 130, 200, 0.06)';
  ctx.lineWidth = 1;
  const step = 50;
  ctx.beginPath();
  for (let x = step; x < ARENA_W; x += step) {
    ctx.moveTo(x, 0); ctx.lineTo(x, ARENA_H);
  }
  for (let y = step; y < ARENA_H; y += step) {
    ctx.moveTo(0, y); ctx.lineTo(ARENA_W, y);
  }
  ctx.stroke();
  // Arena border.
  ctx.strokeStyle = 'rgba(207, 216, 255, 0.30)';
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, ARENA_W - 4, ARENA_H - 4);
}

/** Stateless ambient dust — drifts diagonally across the arena
 *  driven by world.time. ~36 specks, deterministic per-index seed
 *  so they don't dance on resize. */
function drawAmbientDrift(ctx: CanvasRenderingContext2D, time: number): void {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const COUNT = 36;
  for (let i = 0; i < COUNT; i++) {
    const seedA = (i * 91.3 + 17) % ARENA_W;
    const seedB = (i * 47.7 + 41) % ARENA_H;
    const speedX = 5 + (i % 5) * 1.8;
    const speedY = 2 + (i % 3) * 0.8;
    const x = (seedA + time * speedX) % ARENA_W;
    const y = (seedB + time * speedY) % ARENA_H;
    const alpha = 0.10 + 0.06 * Math.sin(time * 1.7 + i * 0.7);
    ctx.fillStyle = `rgba(160, 175, 230, ${alpha})`;
    ctx.beginPath();
    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Tread marks left by moving tanks — small dashes oriented along the
 *  tank's heading at lay-down time. Fade with age. */
function drawTreads(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.treads.length === 0) return;
  ctx.save();
  for (const t of frame.world.treads) {
    const lifeT = Math.max(0, 1 - t.age / 8);
    if (lifeT <= 0) continue;
    ctx.save();
    ctx.translate(t.x, t.y);
    ctx.rotate(t.heading);
    // Two parallel marks for left + right tread.
    ctx.fillStyle = `rgba(20, 16, 28, ${0.55 * lifeT})`;
    ctx.fillRect(-6, -10, 12, 3);
    ctx.fillRect(-6,  7, 12, 3);
    ctx.restore();
  }
  ctx.restore();
}

/** Radial light flashes — drawn additive over everything so they
 *  punch through walls + tanks. Used for kill detonations. */
function drawFlashes(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.flashes.length === 0) return;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const f of frame.world.flashes) {
    const elapsed = frame.world.time - f.startedAt;
    const t = Math.max(0, 1 - elapsed / f.duration);
    if (t <= 0) continue;
    // Ease — sharp punch in, soft fade out.
    const easedAlpha = Math.pow(t, 0.7);
    const easedRadius = f.radius * (1 + (1 - t) * 0.6);
    const grad = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, easedRadius);
    grad.addColorStop(0, `rgba(255, 255, 255, ${0.9 * easedAlpha})`);
    grad.addColorStop(0.3, `rgba(255, 220, 160, ${0.55 * easedAlpha})`);
    grad.addColorStop(0.7, `rgba(255, 138, 58, ${0.20 * easedAlpha})`);
    grad.addColorStop(1, 'rgba(255, 138, 58, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(f.x - easedRadius, f.y - easedRadius, easedRadius * 2, easedRadius * 2);
  }
  ctx.restore();
}

function drawWalls(ctx: CanvasRenderingContext2D): void {
  for (const w of WALLS) {
    ctx.fillStyle = 'rgba(120, 130, 200, 0.18)';
    ctx.fillRect(w.x, w.y, w.w, w.h);
    ctx.strokeStyle = 'rgba(207, 216, 255, 0.45)';
    ctx.lineWidth = 2;
    ctx.strokeRect(w.x + 1, w.y + 1, w.w - 2, w.h - 2);
  }
}

function drawTank(ctx: CanvasRenderingContext2D, tank: Tank, time: number): void {
  if (!tank.alive) {
    // Wreckage marker so the loser's corner has a stain.
    ctx.save();
    ctx.translate(tank.x, tank.y);
    ctx.fillStyle = 'rgba(255, 80, 60, 0.18)';
    ctx.beginPath();
    ctx.arc(0, 0, TANK_RADIUS * 1.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 80, 60, 0.45)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-TANK_RADIUS, -TANK_RADIUS);
    ctx.lineTo(TANK_RADIUS, TANK_RADIUS);
    ctx.moveTo(TANK_RADIUS, -TANK_RADIUS);
    ctx.lineTo(-TANK_RADIUS, TANK_RADIUS);
    ctx.stroke();
    ctx.restore();
    return;
  }
  ctx.save();
  ctx.translate(tank.x, tank.y);

  // Shield (drawn under the tank so the hull still sits on top)
  const shieldActive = time < tank.shieldUntil;
  if (shieldActive) {
    const t = (tank.shieldUntil - time) / SHIELD_DURATION_S;
    ctx.fillStyle = `rgba(120, 220, 255, ${0.18 + 0.12 * t})`;
    ctx.beginPath();
    ctx.arc(0, 0, TANK_RADIUS + 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = `rgba(120, 220, 255, ${0.50 + 0.30 * t})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, TANK_RADIUS + 10, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.rotate(tank.heading);
  // Body (rounded rect) — outer chassis
  const w = 44, h = 30, r = 6;
  ctx.fillStyle = tank.colour;
  ctx.shadowColor = tank.colour;
  ctx.shadowBlur = 12;
  roundedRect(ctx, -w / 2, -h / 2, w, h, r);
  ctx.fill();
  ctx.shadowBlur = 0;
  // Tread strips along top + bottom of the chassis (darker bands with
  // perpendicular tick lines — subtle, sells the "tracked vehicle" read).
  const treadH = 6;
  ctx.fillStyle = 'rgba(10, 10, 18, 0.80)';
  ctx.fillRect(-w / 2, -h / 2, w, treadH);
  ctx.fillRect(-w / 2,  h / 2 - treadH, w, treadH);
  ctx.fillStyle = 'rgba(207, 216, 255, 0.18)';
  for (let i = -w / 2 + 4; i < w / 2 - 2; i += 6) {
    ctx.fillRect(i, -h / 2 + 1, 2, treadH - 2);
    ctx.fillRect(i,  h / 2 - treadH + 1, 2, treadH - 2);
  }
  // Body inner stripe (dark recessed core)
  ctx.fillStyle = 'rgba(10, 10, 18, 0.55)';
  ctx.fillRect(-w / 2 + 4, -h / 2 + treadH + 1, w - 8, h - 2 * treadH - 2);
  // Body outline
  ctx.strokeStyle = 'rgba(10, 10, 18, 0.85)';
  ctx.lineWidth = 1.5;
  roundedRect(ctx, -w / 2, -h / 2, w, h, r);
  ctx.stroke();
  // Centre boss / turret mount — small darker dome with a coloured pip
  ctx.fillStyle = 'rgba(10, 10, 18, 0.95)';
  ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = tank.colour;
  ctx.beginPath(); ctx.arc(0, 0, 4, 0, Math.PI * 2); ctx.fill();
  // Turret barrel
  const bl = 26, bw = 8;
  ctx.fillStyle = 'rgba(10, 10, 18, 0.95)';
  ctx.fillRect(2, -bw / 2, bl, bw);
  ctx.fillStyle = tank.colour;
  ctx.fillRect(4, -bw / 2 + 2, bl - 4, bw - 4);
  // Muzzle highlight — bright dot at the end of the barrel
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.fillRect(bl, -bw / 2 + 2, 2, bw - 4);

  ctx.restore();

  // HP pips above the tank — humans show 4, bots show 3.
  const maxHp = tank.ai ? TANK_MAX_HP : HUMAN_MAX_HP;
  const pipW = 8, pipH = 4, gap = 2;
  const totalW = maxHp * pipW + (maxHp - 1) * gap;
  const startX = tank.x - totalW / 2;
  const y = tank.y - TANK_RADIUS - 14;
  for (let i = 0; i < maxHp; i++) {
    ctx.fillStyle = i < tank.hp ? tank.colour : 'rgba(255,255,255,0.10)';
    ctx.fillRect(startX + i * (pipW + gap), y, pipW, pipH);
  }
}

function drawParticles(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  // Cheap additive draw — each particle is a circle scaled by life
  // fraction with the colour's alpha multiplied by the same fraction.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const p of frame.world.particles) {
    const t = Math.max(0, p.life / p.maxLife);
    ctx.fillStyle = p.colour;
    ctx.globalAlpha = t;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * (0.5 + t * 0.5), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawBullet(ctx: CanvasRenderingContext2D, b: Bullet): void {
  ctx.save();
  // Trail — six fading orbs behind the bullet along its travel vector.
  const speed = Math.hypot(b.vx, b.vy);
  if (speed > 1) {
    const dirX = b.vx / speed, dirY = b.vy / speed;
    const TRAIL_LEN = 32;
    const STEPS = 6;
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 1; i <= STEPS; i++) {
      const t = i / STEPS;
      const tx = b.x - dirX * TRAIL_LEN * t;
      const ty = b.y - dirY * TRAIL_LEN * t;
      ctx.fillStyle = `rgba(255, 235, 180, ${0.40 * (1 - t)})`;
      ctx.beginPath();
      ctx.arc(tx, ty, BULLET_RADIUS * (1.2 - t * 0.7), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }
  ctx.fillStyle = '#fff';
  ctx.shadowColor = '#fff';
  ctx.shadowBlur = 14;
  ctx.beginPath();
  ctx.arc(b.x, b.y, BULLET_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Each player has a dedicated corner of the arena. While un-paired
 *  the corner shows a slim OPEN badge; once a phone is on the slot it
 *  becomes a compact score badge. Same corner either way, so the
 *  "this is my side" mapping holds for the whole match. The QR is no
 *  longer per-corner: drawHeroQr renders the single shared scan code. */
function drawCorners(ctx: CanvasRenderingContext2D, frame: RenderFrame, geom: Geom): void {
  const dpr = ctx.canvas.width / window.innerWidth;
  // Arena bounding box on screen.
  const aLeft = geom.offsetX;
  const aTop = geom.offsetY;
  const aRight = geom.offsetX + ARENA_W * geom.scale;
  const aBottom = geom.offsetY + ARENA_H * geom.scale;
  // QR tile size: large enough to scan from the room, but capped so a
  // 4K display doesn't make them feel cartoonish. Also bounded so two
  // adjacent corners can never touch.
  const inset = 24 * dpr;
  const minTile = 160 * dpr;
  const maxTile = 280 * dpr;
  const arenaW = aRight - aLeft;
  const arenaH = aBottom - aTop;
  // Don't let opposing corners overlap (less than 30% of the arena
  // dimension keeps a healthy gap in the middle for the action).
  const tileSize = Math.max(minTile, Math.min(maxTile, Math.min(arenaW, arenaH) * 0.22));

  for (let slot = 0; slot < MAX_PLAYERS; slot++) {
    const isLeft = (slot % 2) === 0;     // P1, P3 left  / P2, P4 right
    const isTop  = slot < 2;             // P1, P2 top   / P3, P4 bottom
    const x = isLeft ? aLeft + inset           : aRight - inset - tileSize;
    const y = isTop  ? aTop  + inset           : aBottom - inset - tileSize;
    const state = frame.joinState.get(slot) ?? 'open';
    if (state === 'open') {
      drawOpenBadge(ctx, x, y, tileSize, slot, frame, dpr);
    } else {
      drawScoreBadge(ctx, x, y, tileSize, slot, frame, dpr);
    }
  }
}

function drawScoreBadge(
  ctx: CanvasRenderingContext2D,
  cornerX: number, cornerY: number, tileSize: number,
  slot: number, frame: RenderFrame, dpr: number,
): void {
  const tank = frame.world.tanks.get(slot);
  if (!tank) return;
  // Tuck a slim badge in the same outer corner the tile sat in, so
  // the player keeps a clear "you are here" marker without the full
  // QR card occluding the spawn region.
  const badgeW = Math.min(180 * dpr, tileSize);
  const tournamentTotal = !tank.ai ? frame.tournamentTotals.get(slot) : undefined;
  const hasTournament = typeof tournamentTotal === 'number';
  const badgeH = (hasTournament ? 56 : 42) * dpr;
  // Anchor to the outermost corner edge.
  const x = (slot % 2 === 0) ? cornerX : cornerX + tileSize - badgeW;
  const y = (slot < 2)       ? cornerY : cornerY + tileSize - badgeH;
  ctx.save();
  ctx.fillStyle = 'rgba(10, 10, 18, 0.72)';
  ctx.fillRect(x, y, badgeW, badgeH);
  ctx.fillStyle = tank.colour;
  ctx.fillRect(x, y, 6 * dpr, badgeH);
  ctx.fillStyle = tank.colour;
  ctx.font = `bold ${Math.floor(18 * dpr)}px ui-monospace, monospace`;
  ctx.textBaseline = 'middle';
  const labelY = hasTournament ? y + 14 * dpr : y + badgeH / 2;
  const displayName = !tank.ai ? frame.playerNames.get(slot) : null;
  const label = displayName ? `P${slot + 1} ${displayName}` : `P${slot + 1}`;
  ctx.fillText(label, x + 18 * dpr, labelY);
  ctx.fillStyle = '#cfd8ff';
  ctx.font = `${Math.floor(20 * dpr)}px ui-monospace, monospace`;
  ctx.textAlign = 'right';
  ctx.fillText(String(tank.score), x + badgeW - 14 * dpr, labelY);
  if (hasTournament) {
    ctx.fillStyle = '#b48cff';
    ctx.font = `${Math.floor(11 * dpr)}px ui-monospace, monospace`;
    ctx.textAlign = 'left';
    ctx.fillText('TOURNEY', x + 18 * dpr, y + badgeH - 14 * dpr);
    ctx.fillStyle = '#fff';
    ctx.font = `${Math.floor(13 * dpr)}px ui-monospace, monospace`;
    ctx.textAlign = 'right';
    ctx.fillText(String(tournamentTotal), x + badgeW - 14 * dpr, y + badgeH - 14 * dpr);
  }
  ctx.restore();
}

let cachedQrImg: HTMLImageElement | null = null;
let cachedQrSrc: string | null = null;
/** Slim OPEN badge for an un-paired corner. Anchored to the same
 *  outer corner the score badge will later occupy, with a dashed
 *  breathing border so an open slot still reads as live. */
function drawOpenBadge(
  ctx: CanvasRenderingContext2D,
  cornerX: number, cornerY: number, tileSize: number,
  slot: number, frame: RenderFrame, dpr: number,
): void {
  const colour = PLAYER_COLOURS[slot]!;
  const time = frame.world.time;
  const badgeW = Math.min(180 * dpr, tileSize);
  const badgeH = 42 * dpr;
  const x = (slot % 2 === 0) ? cornerX : cornerX + tileSize - badgeW;
  const y = (slot < 2)       ? cornerY : cornerY + tileSize - badgeH;
  // Slot-staggered breathe so the four corners don't pulse in unison.
  const breathe = 0.55 + 0.45 * Math.abs(Math.sin(time * 2.4 + slot * 0.7));
  ctx.save();
  ctx.fillStyle = 'rgba(10, 10, 18, 0.72)';
  ctx.fillRect(x, y, badgeW, badgeH);
  ctx.fillStyle = colour;
  ctx.fillRect(x, y, 6 * dpr, badgeH);
  ctx.strokeStyle = colour;
  ctx.globalAlpha = 0.45 + 0.55 * breathe;
  ctx.lineWidth = 1.5 * dpr;
  ctx.setLineDash([5 * dpr, 4 * dpr]);
  ctx.strokeRect(x + dpr, y + dpr, badgeW - 2 * dpr, badgeH - 2 * dpr);
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
  ctx.fillStyle = colour;
  ctx.font = `bold ${Math.floor(15 * dpr)}px ui-monospace, monospace`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(`P${slot + 1}`, x + 16 * dpr, y + badgeH / 2);
  ctx.fillStyle = '#8a92b8';
  ctx.font = `${Math.floor(12 * dpr)}px ui-monospace, monospace`;
  ctx.textAlign = 'right';
  ctx.fillText('OPEN', x + badgeW - 14 * dpr, y + badgeH / 2);
  ctx.restore();
}

/** One shared hero QR: the single scan target for the table. Every
 *  phone that scans it is handed the next free slot by the broker, so
 *  there is deliberately one code, not one per corner. Sized large
 *  while the lobby is cold, compact once players are in, and hidden
 *  once every slot is full. */
function drawHeroQr(ctx: CanvasRenderingContext2D, frame: RenderFrame, geom: Geom): void {
  let openCount = 0;
  for (let s = 0; s < MAX_PLAYERS; s++) {
    if ((frame.joinState.get(s) ?? 'open') === 'open') openCount += 1;
  }
  if (openCount === 0) return;          // table full, nothing to scan

  const dpr = ctx.canvas.width / window.innerWidth;
  const humans = humanCount(frame.world);
  const time = frame.world.time;
  const cx = geom.offsetX + (ARENA_W * geom.scale) / 2;
  const smaller = Math.min(ctx.canvas.width, ctx.canvas.height);
  // Hero-size while the lobby is cold; compact once a phone is on so
  // the card clears the action but a latecomer can still grab a slot.
  const card = humans === 0
    ? Math.min(440 * dpr, smaller * 0.40)
    : Math.min(230 * dpr, smaller * 0.22);
  const labelH = 68 * dpr;
  const x = cx - card / 2;
  // Centred while cold, tucked under the top edge once play is near.
  // The Math.max keeps it clear of the attract-mode banner.
  const y = humans === 0
    ? (ctx.canvas.height - (card + labelH)) / 2
    : Math.max(geom.offsetY + 22 * dpr, 52 * dpr);

  ctx.save();
  // Ping ring: expands out of the card every 2.2s so the eye snaps to it.
  const phase = (time % 2.2) / 2.2;
  const ringR = card * 0.55 + phase * card * 0.45;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = '#58ff58';
  ctx.globalAlpha = (1 - phase) * 0.5;
  ctx.lineWidth = 3 * dpr;
  ctx.beginPath();
  ctx.arc(cx, y + card / 2, ringR, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
  // Card body + breathing border.
  ctx.fillStyle = 'rgba(10, 10, 18, 0.92)';
  ctx.fillRect(x, y, card, card + labelH);
  const breathe = 0.55 + 0.45 * Math.abs(Math.sin(time * 2.4));
  ctx.strokeStyle = '#58ff58';
  ctx.lineWidth = (2 + breathe * 1.8) * dpr;
  ctx.shadowColor = '#58ff58';
  ctx.shadowBlur = 14 * dpr * breathe;
  ctx.strokeRect(x + dpr, y + dpr, card - 2 * dpr, card + labelH - 2 * dpr);
  ctx.shadowBlur = 0;
  // Header.
  ctx.fillStyle = '#58ff58';
  ctx.font = `bold ${Math.floor(18 * dpr)}px ui-monospace, monospace`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'center';
  ctx.fillText('SCAN TO JOIN', cx, y + 13 * dpr);
  // QR image, cached as an <img> keyed on the data URL.
  if (frame.qrDataUrl) {
    if (cachedQrImg === null || cachedQrSrc !== frame.qrDataUrl) {
      const img = new Image();
      img.src = frame.qrDataUrl;
      cachedQrImg = img;
      cachedQrSrc = frame.qrDataUrl;
    }
    const qrSize = card - 56 * dpr;
    const qx = cx - qrSize / 2;
    const qy = y + 42 * dpr;
    if (cachedQrImg.complete) {
      ctx.drawImage(cachedQrImg, qx, qy, qrSize, qrSize);
    } else {
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(qx, qy, qrSize, qrSize);
    }
  }
  // Sub-lines: the can't-scan fallback. A player opens the pad URL and
  // types the code, so the code is the loud line, the URL the quiet one.
  ctx.fillStyle = '#8a92b8';
  ctx.font = `${Math.floor(12 * dpr)}px ui-monospace, monospace`;
  ctx.fillText('OR ENTER AT PAD.FORGESWORN.DEV', cx, y + card + 12 * dpr);
  ctx.fillStyle = '#58ff58';
  ctx.font = `bold ${Math.floor((humans === 0 ? 30 : 24) * dpr)}px ui-monospace, monospace`;
  ctx.shadowColor = '#58ff58';
  ctx.shadowBlur = 8 * dpr;
  ctx.fillText(formatPairingCode(frame.sessionId), cx, y + card + 32 * dpr);
  ctx.shadowBlur = 0;
  ctx.restore();
}

function drawWinnerOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.round.kind !== 'over') return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const winnerP = frame.world.round.winnerP;
  const winner = winnerP !== null ? frame.world.tanks.get(winnerP) : null;
  const elapsed = frame.world.time - frame.world.round.endedAt;

  ctx.save();
  // Darken-in over 0.4s.
  const overlayAlpha = Math.min(0.55, elapsed * 1.4);
  ctx.fillStyle = `rgba(10, 10, 18, ${overlayAlpha})`;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Confetti cascade — coloured rectangles from above, falling and
  // drifting. Stateless / time-driven so no model changes needed.
  if (elapsed < 5) {
    const STREAMS = 50;
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < STREAMS; i++) {
      const colour = WIN_CONFETTI[i % WIN_CONFETTI.length]!;
      const seedX = (i * 137.13) % 1;
      const swayPhase = (i * 0.29);
      const cycleS = 1.4 + ((i * 0.05) % 0.7);
      const startDelay = (i * 0.024) % 1.2;
      const tNow = elapsed - startDelay;
      if (tNow < 0) continue;
      const yProg = (tNow / cycleS) % 1;
      const x = seedX * ctx.canvas.width + Math.sin(tNow * 2.4 + swayPhase) * 16 * dpr;
      const y = yProg * ctx.canvas.height * 1.1;
      const a = Math.max(0, 0.85 - Math.pow(yProg - 0.55, 2) * 1.6) * Math.min(1, tNow * 1.4);
      if (a <= 0) continue;
      ctx.fillStyle = colour;
      ctx.globalAlpha = a;
      ctx.fillRect(x, y, 4 * dpr, 9 * dpr);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Headline — scales in from 0.7× over 0.45s with overshoot.
  const headT = Math.min(1, elapsed / 0.45);
  const headScale = 0.7 + (1 - Math.pow(1 - headT, 3)) * 0.35 + Math.sin(headT * Math.PI) * 0.06;
  ctx.save();
  ctx.translate(ctx.canvas.width / 2, ctx.canvas.height / 2 - 30 * dpr);
  ctx.scale(headScale, headScale);
  ctx.font = `bold ${Math.floor(96 * dpr)}px ui-monospace, monospace`;
  const winnerName = winner && !winner.ai ? (frame.playerNames.get(winner.p) ?? null) : null;
  const headlineText = winner ? (winnerName ? `${winnerName} WINS` : `P${winner.p + 1} WINS`) : 'DRAW';
  if (winner) {
    ctx.fillStyle = winner.colour;
    ctx.shadowColor = winner.colour;
    ctx.shadowBlur = 28 * dpr;
  } else {
    ctx.fillStyle = '#cfd8ff';
    ctx.shadowBlur = 0;
  }
  ctx.fillText(headlineText, 0, 0);
  ctx.restore();
  ctx.shadowBlur = 0;
  // Sub-line fades in after the headline lands.
  const subAlpha = Math.min(1, Math.max(0, (elapsed - 0.5) * 2));
  ctx.font = `${Math.floor(24 * dpr)}px ui-monospace, monospace`;
  ctx.fillStyle = `rgba(207, 216, 255, ${0.8 * subAlpha})`;
  ctx.fillText('NEXT ROUND...', ctx.canvas.width / 2, ctx.canvas.height / 2 + 60 * dpr);
  ctx.restore();
}
const WIN_CONFETTI = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a', '#ff5fa5', '#5fdbff', '#ffffff'];

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export { SHIELD_COOLDOWN_S, SHIELD_DURATION_S };
